import type { CMSAssetType, CMSAssetVerificationParams } from '../types/index';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@workramp/ui';
import { getCSRFToken } from '~/utils';
import { I18n } from '~/common/explicit-exports';
import { GET_LIST_ASSET_KEY, CMS_ASSET_DETAILS_KEY } from '../constants';

const FILE_UPDATED = I18n.t('js.admin.cms.file_updated');

export type UpdateFileParams = {
  name: string;
  description: string;
  cardBanner?: File;
  id: string;
  allowDownloads: boolean;
  allowInternalSharing: boolean;
  allowExternalSharing: boolean;
  tags?: string[];
  verification?: CMSAssetVerificationParams;
};

const updateFile = async (updateFileParams: UpdateFileParams): Promise<Response> => {
  const data = new FormData();
  // Note: cardBanner will be a valid File object here when user uploads a new file, it will
  // be null when user removes the file, and it will be undefined when user does not change
  // the file (hence don't need to add to our request).
  if (updateFileParams.cardBanner) {
    data.append('cardBanner', updateFileParams.cardBanner);
  } else if (updateFileParams.cardBanner === null) {
    // To remove the file we need to send an empty string to backend.
    data.append('cardBanner', '');
  }

  if (typeof updateFileParams.description === 'string') {
    data.append('description', updateFileParams.description);
  }

  if (updateFileParams.name) {
    data.append('name', updateFileParams.name);
  }

  if (updateFileParams.allowDownloads !== null) {
    data.append('allowDownloads', updateFileParams.allowDownloads.toString());
  }

  if (updateFileParams.allowInternalSharing !== null) {
    data.append('allowInternalSharing', updateFileParams.allowInternalSharing.toString());
  }

  if (updateFileParams.allowExternalSharing !== null) {
    data.append('allowExternalSharing', updateFileParams.allowExternalSharing.toString());
  }

  if (updateFileParams.tags && updateFileParams.tags.length > 0) {
    updateFileParams.tags.forEach((tag) => {
      data.append('tags[]', tag);
    });
  } else {
    // Ensure the `tags[]` field is still included in the request as an empty array
    data.append('tags[]', '');
  }

  if (updateFileParams.verification) {
    data.append('verification[reviewerUserId]', updateFileParams.verification.reviewerUserId);
    data.append('verification[interval]', updateFileParams.verification.interval);
    data.append(
      'verification[isVerifiableByCmsAdmins]',
      updateFileParams.verification.isVerifiableByCmsAdmins?.toString(),
    );
  }

  const url = `/api/cms/file/${updateFileParams.id}`;

  const response = await fetch(url, {
    method: 'PUT',
    body: data,
    headers: {
      contentType: 'multipart/form-data',
      'X-CSRF-TOKEN': getCSRFToken(),
    },
  });

  // Because fetch resolves rather than treat a 400 error as a rejection, we need to manually check for errors and return a rejected promise.
  if (!response.ok) {
    const responseError = await response.json();
    return Promise.reject(new Error(responseError.message));
  }
  return response;
};
export const useUpdateFile = (assetId: string, assetType: CMSAssetType) => {
  const queryClient = useQueryClient();
  const { open: openToast } = useToast();

  return useMutation(updateFile, {
    onSuccess: async () => {
      openToast({
        children: `${FILE_UPDATED}`,
      });
      await queryClient.invalidateQueries([CMS_ASSET_DETAILS_KEY, assetType.toUpperCase(), assetId]);
      await queryClient.invalidateQueries([GET_LIST_ASSET_KEY]);
    },
    onError: (err: Error) => {
      openToast({
        children: err.message,
      });
    },
  });
};
